import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WorkFormPage from '../../../components/work/WorkFormPage';
import WorkManageEntityModal from '../../../components/work/WorkManageEntityModal';
import WorkSelectWithManage from '../../../components/work/WorkSelectWithManage';
import { contractTemplatesApi, contractTypesApi } from '../../../utils/workApi';
import { show422Error } from '../../../utils/api';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { FormSkeleton } from '../../../components/SkeletonLoaders';
import { Button, Card, Form, Input, Select, Row, Col, message } from 'antd';
import WorkPriceInput from '../../../components/work/WorkPriceInput';
import { FileProtectOutlined, SaveOutlined } from '@ant-design/icons';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import { parseContractTypesList } from '../../../utils/workFormUtils';
import { buildContractTemplatePayload } from '../../../utils/workPayloadBuilders';
import { getContractTemplateFromResponse } from '../../../utils/workResponseUtils';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../../hooks/useEntityBreadcrumb';
import {
  mergeBreadcrumbIntoNavigationState,
  trailWithoutLastCrumb,
} from '../../../utils/breadcrumbTrail';

const { TextArea } = Input;
const { Option } = Select;
const label = (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>;

const ContractTemplateForm = () => {
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/work/contracts/templates', { state: buildGoBackState() });
  const [form] = Form.useForm();
  const subjectWatch = Form.useWatch('subject', form);
  const { trail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/work/contracts/templates',
    listLabel: 'قالب‌های قرارداد',
    entityLabel: isEdit
      ? subjectWatch || `ویرایش قالب #${id}`
      : 'قالب قرارداد جدید',
  });
  const goToTemplateView = (templateId) => {
    navigate(`/work/contracts/templates/${templateId}`, {
      state: {
        returnTo: returnToRef.current || '/work/contracts/templates',
        ...buildGoBackState(),
        ...mergeBreadcrumbIntoNavigationState({}, trailWithoutLastCrumb(trail)),
      },
    });
  };
  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [contractTypes, setContractTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [typeModal, setTypeModal] = useState(false);
  const [typeModalOpening, setTypeModalOpening] = useState(false);

  const loadTypes = useCallback(async () => {
    const res = await contractTypesApi.getCreateData();
    const list = parseContractTypesList(res);
    setContractTypes(list);
    return list;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = isEdit
          ? await contractTemplatesApi.getEditData(id)
          : await contractTemplatesApi.getCreateData();
        setContractTypes(parseContractTypesList(res));
        setCurrencies(res?.currencies || []);
        if (isEdit) {
          const tpl = getContractTemplateFromResponse(res);
          if (tpl) {
            form.setFieldsValue({
              subject: tpl.subject,
              description: tpl.description,
              contract_type: tpl.contract_type_id ?? tpl.contract_type?.id,
              amount: tpl.amount,
              currency_id: tpl.currency_id,
            });
          }
        }
      } catch {
        message.error('خطا در بارگذاری');
      } finally {
        setFetching(false);
      }
    })();
  }, [id, isEdit, form]);

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      const body = buildContractTemplatePayload(values);
      if (isEdit) {
        await contractTemplatesApi.update(id, body);
        goToTemplateView(id);
      } else {
        const res = await contractTemplatesApi.create(body);
        const newId = getContractTemplateFromResponse(res)?.id;
        if (newId) goToTemplateView(newId);
        else goBackToList();
      }
      message.success('ذخیره شد');
    } catch (e) {
      show422Error(e, form);
    } finally {
      setSubmitting(false);
    }
  };

  const openTypeModal = async () => {
    setTypeModalOpening(true);
    try {
      await loadTypes();
      setTypeModal(true);
    } finally {
      setTypeModalOpening(false);
    }
  };

  return (
    <WorkFormPage
      title={isEdit ? 'ویرایش قالب قرارداد' : 'قالب قرارداد جدید'}
      subtitle="اطلاعات قالب قرارداد"
      icon={FileProtectOutlined}
      iconClassName="bg-amber-50 dark:bg-amber-900/20"
      iconColorClass="text-amber-600"
      backPath="/work/contracts/templates"
      onBack={goBackToList}
      breadcrumbTrail={trail}
      onBreadcrumbNavigate={goToCrumb}
    >
      {fetching ? (
        <FormSkeleton rows={6} columns={2} />
      ) : (
        <Card className="shadow-lg border-0 bg-white dark:bg-gray-800">
          <Form
            form={form}
            layout="vertical"
            className="work-panel-form"
            onFinish={onFinish}
            onFinishFailed={() => scrollToFirstError(form)}
            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
          >
            <Form.Item
              name="subject"
              label={label('موضوع')}
              rules={[{ required: true, message: 'موضوع الزامی است' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="description" label={label('توضیحات')}>
              <TextArea rows={4} />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <WorkSelectWithManage
                  label="نوع قرارداد"
                  name="contract_type"
                  required
                  rules={[{ required: true, message: 'نوع قرارداد الزامی است' }]}
                  onManageClick={openTypeModal}
                  manageLoading={typeModalOpening}
                >
                  {contractTypes.map((t) => (
                    <Option key={t.id} value={t.id}>
                      {t.name}
                    </Option>
                  ))}
                </WorkSelectWithManage>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="currency_id"
                  label={label('ارز')}
                  rules={[{ required: true, message: 'ارز الزامی است' }]}
                >
                  <Select>
                    {currencies.map((c) => (
                      <Option key={c.id} value={c.id}>
                        {c.currency_name} ({c.currency_symbol})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="amount"
              label={label('مبلغ')}
              rules={[{ required: true, message: 'مبلغ الزامی است' }]}
            >
              <WorkPriceInput />
            </Form.Item>
            <div className="flex justify-end gap-2 pt-4">
              <Button onClick={goBackToList}>انصراف</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<SaveOutlined />}
                className={PANEL_PRIMARY_BTN_CLASS}
              >
                ذخیره
              </Button>
            </div>
          </Form>
        </Card>
      )}

      <WorkManageEntityModal
        open={typeModal}
        onClose={() => setTypeModal(false)}
        title="نوع قرارداد"
        nameField="name"
        loadItems={loadTypes}
        onCreate={(name) => contractTypesApi.create(name)}
        onUpdate={(tid, name) => contractTypesApi.update(tid, name)}
        onDelete={(tid) => contractTypesApi.delete(tid)}
        buildCreatePayload={(v) => v.name}
        buildUpdatePayload={(tid, name) => name}
      />
    </WorkFormPage>
  );
};

export default ContractTemplateForm;
